import React from 'react';
import dayjs from 'dayjs';
import { css } from '@emotion/css';
import { TextField, MenuItem, Divider, Theme, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { useGuestRideStore, type DateRangePreset } from '../stores/guestRideStore';
import { getDateRangeFromPreset, isPickerDisabled } from '../utils/dateRangeUtils';

const GuestRideDateFilter = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { filters, setFilters } = useGuestRideStore();
  const { dateRange, startDate, endDate } = filters;

  const handleRangeChange = (value: string) => {
    const preset = value as DateRangePreset;
    if (preset === 'custom') {
      setFilters({ dateRange: preset, startDate: '', endDate: '' });
    } else {
      const { start, end } = getDateRangeFromPreset(preset);
      setFilters({ dateRange: preset, startDate: start, endDate: end });
    }
  };

  const handleDateChange = (type: 'start' | 'end') => (date: dayjs.Dayjs | null) => {
    if (!date || !date.isValid()) return;
    const iso = type === 'start' ? date.startOf('day').format() : date.endOf('day').format();
    setFilters({ [type === 'start' ? 'startDate' : 'endDate']: iso });
  };

  const pickersDisabled = isPickerDisabled(dateRange);

  return (
    <div className={cls.wrapper}>
      <TextField
        select
        label='Date Range'
        placeholder='Select a date range...'
        variant='outlined'
        size='small'
        value={dateRange}
        onChange={e => handleRangeChange(e.target.value)}
        sx={{ minWidth: 200 }}
      >
        <MenuItem value='today'>
          <span className={cls.rangeText}>Today</span>
          <span className={cls.rangeSubText}>{dayjs().format('MM/DD/YYYY')}</span>
        </MenuItem>
        <MenuItem value='yesterday'>
          <span className={cls.rangeText}>Yesterday</span>
          <span className={cls.rangeSubText}>{dayjs().subtract(1, 'day').format('MM/DD/YYYY')}</span>
        </MenuItem>

        <Divider />

        <MenuItem value='thisweek'>
          <span className={cls.rangeText}>This Week</span>
          <span className={cls.rangeSubText}>
            {dayjs().startOf('week').format('MM/DD/YYYY')} - {dayjs().endOf('week').format('MM/DD/YYYY')}
          </span>
        </MenuItem>
        <MenuItem value='lastweek'>
          <span className={cls.rangeText}>Last Week</span>
          <span className={cls.rangeSubText}>
            {dayjs().subtract(1, 'week').startOf('week').format('MM/DD/YYYY')} -{' '}
            {dayjs().subtract(1, 'week').endOf('week').format('MM/DD/YYYY')}
          </span>
        </MenuItem>

        <Divider />

        <MenuItem value='thismonth'>
          <span className={cls.rangeText}>This Month</span>
          <span className={cls.rangeSubText}>
            {dayjs().startOf('month').format('MM/DD/YYYY')} - {dayjs().endOf('month').format('MM/DD/YYYY')}
          </span>
        </MenuItem>
        <MenuItem value='lastmonth'>
          <span className={cls.rangeText}>Last Month</span>
          <span className={cls.rangeSubText}>
            {dayjs().subtract(1, 'month').startOf('month').format('MM/DD/YYYY')} -{' '}
            {dayjs().subtract(1, 'month').endOf('month').format('MM/DD/YYYY')}
          </span>
        </MenuItem>

        <Divider />

        <MenuItem value='custom'>Custom</MenuItem>
      </TextField>

      <DatePicker
        disabled={pickersDisabled}
        maxDate={endDate ? dayjs(endDate) : undefined}
        label='Start Date'
        orientation='portrait'
        value={startDate ? dayjs(startDate) : null}
        onChange={handleDateChange('start')}
        slotProps={{
          textField: {
            size: 'small',
            variant: 'outlined',
            sx: { width: 160 },
          },
        }}
      />

      <DatePicker
        disabled={pickersDisabled}
        minDate={startDate ? dayjs(startDate) : undefined}
        label='End Date'
        orientation='portrait'
        value={endDate ? dayjs(endDate) : null}
        onChange={handleDateChange('end')}
        slotProps={{
          textField: {
            size: 'small',
            variant: 'outlined',
            sx: { width: 160 },
          },
        }}
      />
    </div>
  );
};

const useStyles = (theme: Theme) => ({
  wrapper: css`
    display: flex;
    align-items: center;
    gap: 10px;
  `,
  rangeText: css`
    margin-right: 6px;
  `,
  rangeSubText: css`
    font-size: 12px;
    color: ${theme.palette.text.disabled};
  `,
});

export default GuestRideDateFilter;
